import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link2, Loader2 } from 'lucide-react';
import { resolveSupportShortLink } from '../../Admin/Services/SupportService';

/**
 * Landing page for the short form `{site}/l/{slug}`.
 *
 * The backend resolves the slug to the full UTM-tagged URL and returns it as JSON rather than
 * issuing a redirect, so the browser lands on the destination through `window.location.replace`.
 * That keeps the short address out of the user's history and preserves the tracking parameters.
 *
 * No auth is required: the resolver is a public endpoint because the link is supposed to work
 * for anyone who received the message.
 */
const ShortLinkRedirectPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<'loading' | 'found' | 'missing'>('loading');
  const started = useRef(false);

  useEffect(() => {
    if (!slug || started.current) return;
    started.current = true;

    let cancelled = false;
    let redirectTo: string | null = null;

    (async () => {
      try {
        const response = await resolveSupportShortLink(slug);
        const url = response.data?.url;
        if (!cancelled && response.success && url) {
          setState('found');
          redirectTo = url;
        } else {
          setState('missing');
        }
      } catch {
        if (!cancelled) setState('missing');
      }

      if (!cancelled && redirectTo) {
        window.location.replace(redirectTo);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          {state === 'loading' ? (
            <Loader2 size={26} className="animate-spin" />
          ) : (
            <Link2 size={26} />
          )}
        </span>

        {state === 'loading' && (
          <>
            <h1 className="mt-5 text-lg font-black text-slate-100">Taking you there</h1>
            <p className="mt-2 text-xs font-semibold text-slate-400">
              Resolving the link to its tracking URL…
            </p>
          </>
        )}

        {state === 'missing' && (
          <>
            <h1 className="mt-5 text-lg font-black text-slate-100">Link not found</h1>
            <p className="mt-2 text-xs font-semibold text-slate-400">
              This short link does not exist or was retired by the owner.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ShortLinkRedirectPage;